import React, { useEffect, useState } from 'react';
import { HeroBanner } from '../components/HeroBanner';
import { QuickAction } from '../components/QuickAction';
import { DailyChallenge } from '../components/DailyChallenge';
import { BottomNav } from '../components/BottomNav';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useApp } from '../context/AppContext';
import { Settings, Bike, Coffee, Recycle, Zap, Info, X, Gem, Star, Check, Shield, Award, FileCheck, Clock } from 'lucide-react';
interface HomePageProps {
  onNavigate: (page: string, actionId?: string) => void;
}
export function HomePage({
  onNavigate
}: HomePageProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showImpactModal, setShowImpactModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isCreditsClosing, setIsCreditsClosing] = useState(false);
  const {
    wallet,
    actions
  } = useApp();
  // Filter actions to show planned + pending_approval
  const visibleActions = actions.filter(action => action.status === 'planned' || action.status === 'pending_approval');
  // Get user's action titles to filter suggested actions
  const userActionTitles = actions.map(a => a.title.toLowerCase());
  // Suggested actions - filter out ones user already has
  const allSuggestedActions = [{
    emoji: '💡',
    title: 'Installare lampadine LED',
    points: 20,
    type: 'gold' as const,
    tag: '💡 Facile da iniziare'
  }, {
    emoji: '🌳',
    title: 'Piantare alberi',
    points: 80,
    type: 'diamond' as const,
    tag: '🔥 Popolare nella community'
  }, {
    emoji: '🚌',
    title: 'Usare i mezzi pubblici',
    points: 30,
    type: 'gold' as const,
    tag: '✨ Simile alle tue azioni'
  }, {
    emoji: '🍎',
    title: 'Comprare prodotti locali',
    points: 25,
    type: 'gold' as const,
    tag: '💡 Facile da iniziare'
  }, {
    emoji: '🚿',
    title: 'Fare docce brevi',
    points: 15,
    type: 'gold' as const,
    tag: '✨ Simile alle tue azioni'
  }];
  const suggestedActions = allSuggestedActions.filter(action => !userActionTitles.includes(action.title.toLowerCase())).slice(0, 3);
  const handleCloseModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowImpactModal(false);
      setIsClosing(false);
    }, 300);
  };
  const handleCloseCreditsModal = () => {
    setIsCreditsClosing(true);
    setTimeout(() => {
      setShowCreditsModal(false);
      setIsCreditsClosing(false);
    }, 300);
  };
  useEffect(() => {
    const scrollArea = document.querySelector('[data-radix-scroll-area-viewport]');
    const handleScroll = () => {
      if (scrollArea) {
        setIsScrolled(scrollArea.scrollTop > 280);
      }
    };
    if (scrollArea) {
      scrollArea.addEventListener('scroll', handleScroll);
      return () => scrollArea.removeEventListener('scroll', handleScroll);
    }
  }, []);
  return <div className="flex flex-col h-screen bg-gray-50 font-sans text-gray-900" data-id="element-501">
      {/* Transparent Header */}
      <header className={`fixed top-0 left-0 right-0 px-6 py-4 flex justify-between items-center z-30 transition-all duration-300 ${isScrolled ? 'bg-white border-b border-gray-100' : 'bg-transparent'}`} data-id="element-502">
        <div className="flex items-center" data-id="element-503">
          <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback Logo" className={`h-8 w-auto transition-all duration-300 ${isScrolled ? '' : 'brightness-0 invert'}`} data-id="element-504" />
        </div>
        <div className="flex items-center gap-3" data-id="element-505">
          <button onClick={() => setShowCreditsModal(true)} className={`flex items-center gap-1.5 transition-colors duration-300 cursor-pointer hover:opacity-80 ${isScrolled ? 'text-green-600' : 'text-white'}`} data-id="element-506">
            <Gem className="h-5 w-5" strokeWidth={2} data-id="element-507" />
            <span className="text-sm font-normal" data-id="element-508">{wallet.diamonds}</span>
          </button>
          <button onClick={() => setShowCreditsModal(true)} className={`flex items-center gap-1.5 transition-colors duration-300 cursor-pointer hover:opacity-80 ${isScrolled ? 'text-gray-600' : 'text-white'}`} data-id="element-509">
            <Star className="h-5 w-5" strokeWidth={2} data-id="element-510" />
            <span className="text-sm font-normal" data-id="element-511">{wallet.stars}</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <ScrollArea className="flex-1" data-id="element-512">
        {/* Hero Banner - Scrolls with content */}
        <HeroBanner name="Alex" avatar="https://cdn.magicpatterns.com/uploads/wAuXNp8Njv5DkmF4gEUeC7/jonathan-cooper-ViyA5myhBVw-unsplash.jpg" weeklyImpact={85} onShowCommunityImpact={() => setShowImpactModal(true)} data-id="element-513" />

        <div className="px-6 py-6 space-y-8 max-w-md mx-auto pb-28" data-id="element-514">
          {/* GB Intelligence CTA */}
          <div className="p-[2px]" style={{
          background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 100%)',
          borderRadius: '100px'
        }} data-id="element-515">
            <button onClick={() => onNavigate('gb-intelligence')} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white hover:bg-gray-50 transition-colors font-medium text-sm text-gray-900" style={{
            borderRadius: '100px'
          }} data-id="element-516">
              Chiedi a GB Intelligence
            </button>
          </div>

          {/* Planned Actions Reminder Widget */}
          <section data-id="element-517">
            <div className="bg-white rounded-xl p-5 border border-gray-100" data-id="element-518">
              <div className="flex items-center justify-between mb-4" data-id="element-519">
                <div data-id="element-520">
                  <h2 className="font-normal text-lg text-gray-900" data-id="element-521">
                    Azioni pianificate
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-522">
                    {visibleActions.filter(a => a.status === 'planned').length}{' '}
                    di {visibleActions.length} da completare
                  </p>
                </div>
              </div>

              {/* Planned Actions List */}
              <div className="space-y-3" data-id="element-523">
                {visibleActions.map(action => <div key={action.id} className="flex flex-col gap-2 p-3 rounded-xl border border-gray-100 hover:border-green-500 transition-colors bg-white" data-id="element-524">
                    <div className="flex items-start gap-2" data-id="element-525">
                      <div className="text-2xl" data-id="element-526">{action.emoji}</div>
                      <div className="flex-1" data-id="element-527">
                        <p className="font-normal text-sm text-gray-900" data-id="element-528">
                          {action.title}
                        </p>
                        {action.scheduledAt && <p className="text-xs text-gray-500" data-id="element-529">
                            📅 {action.scheduledAt}
                          </p>}

                        {/* Show filename if proof exists */}
                        {action.proof && <div className="flex items-center gap-1 mt-1" data-id="element-530">
                            <FileCheck className="h-3 w-3 text-gray-400" data-id="element-531" />
                            <p className="text-xs text-gray-500" data-id="element-532">
                              {action.proof.filename}
                            </p>
                          </div>}
                      </div>
                    </div>

                    <div className="flex items-center justify-between pl-9" data-id="element-533">
                      {/* Pending badge */}
                      {action.status === 'pending_approval' && <span className="flex items-center gap-1 text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full" data-id="element-534">
                          <Clock className="h-3 w-3" data-id="element-535" />
                          In attesa
                        </span>}

                      {/* Certifica button */}
                      {action.status === 'planned' && <div className="ml-auto" data-id="element-536">
                          <button onClick={() => onNavigate('actions', action.id)} className="px-3 py-1.5 rounded-full text-xs font-normal transition-all bg-white border-2 border-green-600 text-green-600 hover:bg-green-50" data-id="element-537">
                            <span className="flex items-center gap-1" data-id="element-538">
                              {action.type === 'diamond' ? 'Certifica' : 'Verifica'}
                              {action.type === 'diamond' ? <Gem className="h-3 w-3" strokeWidth={2} data-id="element-539" /> : <Star className="h-3 w-3" strokeWidth={2} data-id="element-540" />}
                              <span data-id="element-541">+{action.points}</span>
                            </span>
                          </button>
                        </div>}

                      {/* Disabled button for pending */}
                      {action.status === 'pending_approval' && <div className="ml-auto" data-id="element-542">
                          <button disabled className="px-3 py-1.5 rounded-full text-xs font-normal bg-gray-100 text-gray-400 cursor-not-allowed" data-id="element-543">
                            In verifica
                          </button>
                        </div>}
                    </div>
                  </div>)}
              </div>

              {/* Add More Link */}
              <button onClick={() => onNavigate('impact')} className="w-full mt-4 text-sm text-green-600 font-normal hover:underline text-center" data-id="element-544">
                Pianifica altre azioni
              </button>
            </div>
          </section>

          {/* Suggested Actions Widget */}
          {suggestedActions.length > 0 && <section data-id="element-545">
              <div className="bg-white rounded-xl p-5 border border-gray-100" data-id="element-546">
                <div className="flex items-center justify-between mb-4" data-id="element-547">
                  <div data-id="element-548">
                    <h2 className="font-normal text-lg" data-id="element-549">Suggerite per te</h2>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-550">
                      Basate sulla community e le tue abitudini
                    </p>
                  </div>
                </div>

                {/* Suggested Actions List */}
                <div className="space-y-3" data-id="element-551">
                  {suggestedActions.map((action, index) => <div key={index} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-green-500 transition-colors cursor-pointer" data-id="element-552">
                      <div className="text-2xl" data-id="element-553">{action.emoji}</div>
                      <div className="flex-1" data-id="element-554">
                        <p className="font-normal text-sm text-gray-900" data-id="element-555">
                          {action.title}
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-556">{action.tag}</p>
                      </div>
                      <div className={`flex items-center gap-1 text-xs font-normal ${action.type === 'diamond' ? 'text-green-600' : 'text-gray-400'}`} data-id="element-557">
                        {action.type === 'diamond' ? <Gem className="h-3.5 w-3.5" strokeWidth={2} data-id="element-558" /> : <Star className="h-3.5 w-3.5" strokeWidth={2} data-id="element-559" />}
                        <span data-id="element-560">+{action.points}</span>
                      </div>
                    </div>)}
                </div>

                {/* View All Link */}
                <button onClick={() => onNavigate('impact')} className="w-full mt-4 text-sm text-green-600 font-normal hover:underline text-center" data-id="element-561">
                  Vedi tutte le azioni
                </button>
              </div>
            </section>}

          {/* Recent Activity */}
          <section data-id="element-562">
            <h2 className="font-normal text-lg mb-3" data-id="element-563">Attività recenti</h2>
            <div className="bg-white rounded-xl p-4 border border-gray-100 space-y-4" data-id="element-564">
              <div className="flex items-center justify-between group cursor-pointer hover:bg-gray-50 p-2 -mx-2 rounded-lg transition-colors" data-id="element-565">
                <div className="flex items-center gap-3" data-id="element-566">
                  <div className="text-2xl" data-id="element-567">🚲</div>
                  <div data-id="element-568">
                    <p className="font-normal text-sm text-gray-900" data-id="element-569">
                      In bici al lavoro
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-570">Oggi, 8:30</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-sm font-normal text-green-600" data-id="element-571">
                  <Gem className="h-4 w-4" strokeWidth={2} data-id="element-572" />
                  <span data-id="element-573">+50</span>
                </div>
              </div>

              <div className="h-px bg-gray-100 w-full" data-id="element-574"></div>

              <div className="flex items-center justify-between group cursor-pointer hover:bg-gray-50 p-2 -mx-2 rounded-lg transition-colors" data-id="element-575">
                <div className="flex items-center gap-3" data-id="element-576">
                  <div className="text-2xl" data-id="element-577">♻️</div>
                  <div data-id="element-578">
                    <p className="font-normal text-sm text-gray-900" data-id="element-579">
                      Riciclato il vetro
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-580">Ieri, 14:20</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-sm font-normal text-gray-400" data-id="element-581">
                  <Star className="h-4 w-4" strokeWidth={2} data-id="element-582" />
                  <span data-id="element-583">+10</span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </ScrollArea>

      {/* Navigation */}
      <BottomNav activePage="home" onNavigate={onNavigate} data-id="element-584" />

      {/* Credits Explanation Modal */}
      {showCreditsModal && <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={handleCloseCreditsModal} data-id="element-585" />

          {/* Bottom Sheet */}
          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto max-h-[85vh] overflow-hidden flex flex-col border-t border-gray-100 ${isCreditsClosing ? 'animate-slide-down' : 'animate-slide-up'}`} data-id="element-586">
            {/* Fixed Header */}
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-587">
              {/* Handle Bar */}
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" data-id="element-588" />

              {/* Header */}
              <div className="flex items-center justify-between" data-id="element-589">
                <h3 className="text-xl font-normal text-gray-900" data-id="element-590">
                  Come funzionano i crediti
                </h3>
                <button onClick={handleCloseCreditsModal} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-591">
                  <X className="h-6 w-6" strokeWidth={1.5} data-id="element-592" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-593">
              <div className="space-y-6" data-id="element-594">
                {/* Verification Levels */}
                <div data-id="element-595">
                  <h4 className="font-normal text-base text-gray-900 mb-4" data-id="element-596">
                    Livelli di verifica delle azioni
                  </h4>
                  <div className="space-y-3" data-id="element-597">
                    {/* Not Verified */}
                    <div className="bg-white rounded-xl p-4 border border-gray-200" data-id="element-598">
                      <div className="flex items-start gap-3" data-id="element-599">
                        <FileCheck className="h-5 w-5 text-gray-400 flex-shrink-0" strokeWidth={2} data-id="element-600" />
                        <div className="flex-1" data-id="element-601">
                          <h5 className="font-normal text-sm text-gray-900 mb-1" data-id="element-602">
                            Non verificate
                          </h5>
                          <p className="text-xs text-gray-600 leading-relaxed" data-id="element-603">
                            Azioni tracciate per statistiche e profilazione. Non
                            danno crediti.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Verified */}
                    <div className="bg-white rounded-xl p-4 border border-gray-200" data-id="element-604">
                      <div className="flex items-start gap-3" data-id="element-605">
                        <Award className="h-5 w-5 text-yellow-500 flex-shrink-0" strokeWidth={2} data-id="element-606" />
                        <div className="flex-1" data-id="element-607">
                          <h5 className="font-normal text-sm text-gray-900 mb-1" data-id="element-608">
                            Verificate
                          </h5>
                          <p className="text-xs text-gray-600 leading-relaxed" data-id="element-609">
                            Verifica tramite auto-certificazione. Danno crediti
                            Stelle.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Certified */}
                    <div className="bg-white rounded-xl p-4 border border-gray-200" data-id="element-610">
                      <div className="flex items-start gap-3" data-id="element-611">
                        <Shield className="h-5 w-5 text-green-600 flex-shrink-0" strokeWidth={2} data-id="element-612" />
                        <div className="flex-1" data-id="element-613">
                          <h5 className="font-normal text-sm text-gray-900 mb-1" data-id="element-614">
                            Certificate
                          </h5>
                          <p className="text-xs text-gray-600 leading-relaxed" data-id="element-615">
                            Certificate da Greenback o partner ufficiali.
                            Registrate su Blockchain. Danno crediti Diamond.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Credit Types */}
                <div data-id="element-616">
                  <h4 className="font-normal text-base text-gray-900 mb-4" data-id="element-617">
                    Cosa puoi fare con i crediti
                  </h4>
                  <div className="space-y-3" data-id="element-618">
                    {/* Star Credits */}
                    <div className="bg-white rounded-xl p-5 border border-gray-200" data-id="element-619">
                      <div className="flex items-start gap-3 mb-3" data-id="element-620">
                        <Star className="h-6 w-6 text-yellow-500 flex-shrink-0" strokeWidth={2} fill="currentColor" data-id="element-621" />
                        <div className="flex-1" data-id="element-622">
                          <h4 className="font-normal text-base text-gray-900 mb-1" data-id="element-623">
                            Crediti Stelle
                          </h4>
                          <p className="text-xs text-gray-600 leading-relaxed" data-id="element-624">
                            Valore non monetario
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2" data-id="element-625">
                        <p className="text-xs text-gray-700" data-id="element-626">
                          <span className="font-medium" data-id="element-627">✓</span> Partecipazione
                          a eventi esclusivi
                        </p>
                        <p className="text-xs text-gray-700" data-id="element-628">
                          <span className="font-medium" data-id="element-629">✓</span> Sblocco di
                          feature premium nell'app
                        </p>
                        <p className="text-xs text-gray-700" data-id="element-630">
                          <span className="font-medium" data-id="element-631">✓</span> Accesso a
                          contenuti riservati
                        </p>
                      </div>
                    </div>

                    {/* Diamond Credits */}
                    <div className="bg-white rounded-xl p-5 border border-gray-200" data-id="element-632">
                      <div className="flex items-start gap-3 mb-3" data-id="element-633">
                        <Gem className="h-6 w-6 text-green-600 flex-shrink-0" strokeWidth={2} data-id="element-634" />
                        <div className="flex-1" data-id="element-635">
                          <h4 className="font-normal text-base text-gray-900 mb-1" data-id="element-636">
                            Crediti Diamond
                          </h4>
                          <p className="text-xs text-gray-600 leading-relaxed" data-id="element-637">
                            Valore monetario e ufficiale
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2" data-id="element-638">
                        <p className="text-xs text-gray-700" data-id="element-639">
                          <span className="font-medium" data-id="element-640">✓</span> Convertibili in
                          buoni acquisto
                        </p>
                        <p className="text-xs text-gray-700" data-id="element-641">
                          <span className="font-medium" data-id="element-642">✓</span> Convertibili in
                          denaro
                        </p>
                        <p className="text-xs text-gray-700" data-id="element-643">
                          <span className="font-medium" data-id="element-644">✓</span> Certificazione
                          su Blockchain
                        </p>
                        <p className="text-xs text-gray-700" data-id="element-645">
                          <span className="font-medium" data-id="element-646">✓</span> Riconoscimento
                          ufficiale e formale
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200" data-id="element-647">
                  <div className="flex items-start gap-3" data-id="element-648">
                    <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" strokeWidth={2} data-id="element-649" />
                    <div data-id="element-650">
                      <p className="text-xs text-blue-900 leading-relaxed" data-id="element-651">
                        <span className="font-medium" data-id="element-652">Nota:</span> Le azioni
                        certificate richiedono una verifica ufficiale da parte
                        di Greenback o dei nostri partner autorizzati. Una volta
                        certificate, vengono registrate permanentemente su
                        Blockchain per garantire trasparenza e autenticità.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* Impact Details Modal */}
      {showImpactModal && <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={handleCloseModal} data-id="element-653" />

          {/* Bottom Sheet */}
          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto max-h-[75vh] overflow-hidden flex flex-col border-t border-gray-100 ${isClosing ? 'animate-slide-down' : 'animate-slide-up'}`} data-id="element-654">
            {/* Fixed Header */}
            <div className="flex-shrink-0 px-6 pt-6 pb-4" data-id="element-655">
              {/* Handle Bar */}
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" data-id="element-656" />

              {/* Header */}
              <div className="flex items-center justify-between" data-id="element-657">
                <h3 className="text-xl font-normal text-gray-900" data-id="element-658">
                  Impatto della community
                </h3>
                <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-659">
                  <X className="h-6 w-6" strokeWidth={1.5} data-id="element-660" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-6 pb-8" data-id="element-661">
              {/* Content */}
              <div className="space-y-6" data-id="element-662">
                {/* Impact Comparison - Side by Side */}
                <div className="grid grid-cols-2 gap-3" data-id="element-663">
                  {/* Your Impact */}
                  <div className="bg-white rounded-xl p-4 border border-gray-100" data-id="element-664">
                    <div className="text-center" data-id="element-665">
                      <p className="text-xs text-gray-500 mb-2" data-id="element-666">
                        Il Tuo Impatto
                      </p>
                      <p className="text-3xl font-normal text-green-600" data-id="element-667">85%</p>
                      <p className="text-xs text-gray-400 mt-1" data-id="element-668">12 azioni</p>
                    </div>
                  </div>

                  {/* Community Average */}
                  <div className="bg-white rounded-xl p-4 border border-gray-100" data-id="element-669">
                    <div className="text-center" data-id="element-670">
                      <p className="text-xs text-gray-500 mb-2" data-id="element-671">Community</p>
                      <p className="text-3xl font-normal text-gray-600" data-id="element-672">72%</p>
                      <p className="text-xs text-gray-400 mt-1" data-id="element-673">8.5 media</p>
                    </div>
                  </div>
                </div>

                {/* Popular Community Actions */}
                <div data-id="element-674">
                  <h4 className="text-sm font-normal text-gray-900 mb-4" data-id="element-675">
                    🌍 Azioni più popolari questa settimana
                  </h4>
                  <div className="space-y-4" data-id="element-676">
                    {/* Bike Commute - User completed */}
                    <div className="bg-green-50 rounded-xl p-4 border border-green-100" data-id="element-677">
                      <div className="flex items-center gap-3 mb-2" data-id="element-678">
                        <div className="text-2xl" data-id="element-679">🚲</div>
                        <div className="flex-1" data-id="element-680">
                          <div className="flex items-center justify-between mb-1" data-id="element-681">
                            <span className="text-sm font-normal text-gray-900" data-id="element-682">
                              Andare al lavoro in bici
                            </span>
                            <span className="text-xs font-normal text-gray-700" data-id="element-683">
                              3.240 utenti
                            </span>
                          </div>
                          <div className="w-full bg-green-200/50 rounded-full h-1.5" data-id="element-684">
                            <div className="bg-green-600 h-full rounded-full" style={{
                          width: '85%'
                        }} data-id="element-685" />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-11" data-id="element-686">
                        <div className="h-1 w-1 rounded-full bg-green-600" data-id="element-687"></div>
                        <span className="text-xs font-normal text-green-700" data-id="element-688">
                          Hai fatto: 3 volte questa settimana
                        </span>
                      </div>
                    </div>

                    {/* Recycling - User completed */}
                    <div className="bg-green-50 rounded-xl p-4 border border-green-100" data-id="element-689">
                      <div className="flex items-center gap-3 mb-2" data-id="element-690">
                        <div className="text-2xl" data-id="element-691">♻️</div>
                        <div className="flex-1" data-id="element-692">
                          <div className="flex items-center justify-between mb-1" data-id="element-693">
                            <span className="text-sm font-normal text-gray-900" data-id="element-694">
                              Riciclo
                            </span>
                            <span className="text-xs font-normal text-gray-700" data-id="element-695">
                              2.890 utenti
                            </span>
                          </div>
                          <div className="w-full bg-green-200/50 rounded-full h-1.5" data-id="element-696">
                            <div className="bg-green-600 h-full rounded-full" style={{
                          width: '76%'
                        }} data-id="element-697" />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-11" data-id="element-698">
                        <div className="h-1 w-1 rounded-full bg-green-600" data-id="element-699"></div>
                        <span className="text-xs font-normal text-green-700" data-id="element-700">
                          Tu: 3 volte questa settimana
                        </span>
                      </div>
                    </div>

                    {/* Reusable Cup - User completed */}
                    <div className="bg-green-50 rounded-xl p-4 border border-green-100" data-id="element-701">
                      <div className="flex items-center gap-3 mb-2" data-id="element-702">
                        <div className="text-2xl" data-id="element-703">☕</div>
                        <div className="flex-1" data-id="element-704">
                          <div className="flex items-center justify-between mb-1" data-id="element-705">
                            <span className="text-sm font-normal text-gray-900" data-id="element-706">
                              Tazza Riutilizzabile
                            </span>
                            <span className="text-xs font-normal text-gray-700" data-id="element-707">
                              2.120 utenti
                            </span>
                          </div>
                          <div className="w-full bg-green-200/50 rounded-full h-1.5" data-id="element-708">
                            <div className="bg-green-600 h-full rounded-full" style={{
                          width: '56%'
                        }} data-id="element-709" />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-11" data-id="element-710">
                        <div className="h-1 w-1 rounded-full bg-green-600" data-id="element-711"></div>
                        <span className="text-xs font-normal text-green-700" data-id="element-712">
                          Hai fatto: 2 volte questa settimana
                        </span>
                      </div>
                    </div>

                    {/* Energy Saving - User NOT completed */}
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-713">
                      <div className="flex items-center gap-3 mb-2" data-id="element-714">
                        <div className="text-2xl opacity-50" data-id="element-715">⚡</div>
                        <div className="flex-1" data-id="element-716">
                          <div className="flex items-center justify-between mb-1" data-id="element-717">
                            <span className="text-sm font-normal text-gray-900" data-id="element-718">
                              Risparmio Energetico
                            </span>
                            <span className="text-xs font-normal text-gray-700" data-id="element-719">
                              1.850 utenti
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5" data-id="element-720">
                            <div className="bg-gray-400 h-full rounded-full" style={{
                          width: '49%'
                        }} data-id="element-721" />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-11" data-id="element-722">
                        <div className="h-1 w-1 rounded-full bg-gray-400" data-id="element-723"></div>
                        <span className="text-xs font-normal text-gray-500" data-id="element-724">
                          Non ancora completata
                        </span>
                      </div>
                    </div>

                    {/* Plant Trees - User NOT completed */}
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-725">
                      <div className="flex items-center gap-3 mb-2" data-id="element-726">
                        <div className="text-2xl opacity-50" data-id="element-727">🌳</div>
                        <div className="flex-1" data-id="element-728">
                          <div className="flex items-center justify-between mb-1" data-id="element-729">
                            <span className="text-sm font-normal text-gray-900" data-id="element-730">
                              Piantare Alberi
                            </span>
                            <span className="text-xs font-normal text-gray-700" data-id="element-731">
                              1.420 utenti
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5" data-id="element-732">
                            <div className="bg-gray-400 h-full rounded-full" style={{
                          width: '37%'
                        }} data-id="element-733" />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-11" data-id="element-734">
                        <div className="h-1 w-1 rounded-full bg-gray-400" data-id="element-735"></div>
                        <span className="text-xs font-normal text-gray-500" data-id="element-736">
                          Non ancora completata
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Encouragement */}
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4 text-center border border-green-100" data-id="element-737">
                  <p className="text-sm font-normal text-green-800" data-id="element-738">
                    🌟 Hai completato 3 delle 5 azioni più popolari!
                  </p>
                  <p className="text-xs text-green-700 mt-1" data-id="element-739">
                    Continua così per restare al top della community
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>}
    </div>;
}